// src/components/finance/TrialBalance.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch, exportFinance } from '../../api.js'
import FinanceNav from './FinanceNav.jsx'

/**
 * TrialBalance.jsx (FEDIS)
 * ------------------------------------------------------------
 * Balance (débit = crédit) pour vérifier la propreté comptable.
 *
 * API attendue:
 *  - GET /api/finance/trial-balance?from=YYYY-MM-DD&to=YYYY-MM-DD
 *    -> { rows: [{ code, label, debit, credit, balance }], totals? }
 */

function toNum(v, def = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : def
}

function isoDate(d) {
  const dt = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toISOString().slice(0, 10)
}

function fmtDateFR(d) {
  if (!d) return '—'
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return '—'
  return dt.toLocaleDateString('fr-FR')
}

function fmtMoney(v) {
  return `${toNum(v, 0).toLocaleString('fr-FR')} XOF`
}

function normalizeRows(payload) {
  // Supporte: { rows: [...] } ou [...] ou { data: [...] }
  if (Array.isArray(payload?.rows)) return payload.rows
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

function SkeletonRow() {
  return (
    <div className="grid grid-cols-12 gap-3 p-3 border border-slate-200 rounded-lg bg-white">
      <div className="col-span-2 h-4 bg-slate-100 rounded animate-pulse" />
      <div className="col-span-4 h-4 bg-slate-100 rounded animate-pulse" />
      <div className="col-span-2 h-4 bg-slate-100 rounded animate-pulse" />
      <div className="col-span-2 h-4 bg-slate-100 rounded animate-pulse" />
      <div className="col-span-2 h-4 bg-slate-100 rounded animate-pulse" />
    </div>
  )
}

export default function TrialBalance() {
  const defaultRange = useMemo(() => {
    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth(), 1)
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return { from: isoDate(from), to: isoDate(to) }
  }, [])

  const [from, setFrom] = useState(defaultRange.from)
  const [to, setTo] = useState(defaultRange.to)
  const [q, setQ] = useState('')

  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [hint, setHint] = useState(null)

  const [rows, setRows] = useState([])

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    if (!s) return rows
    return rows.filter(r => {
      const hay = `${r?.code || ''} ${r?.label || ''}`.toLowerCase()
      return hay.includes(s)
    })
  }, [rows, q])

  const totals = useMemo(() => {
    const debit = rows.reduce((acc, r) => acc + toNum(r?.debit), 0)
    const credit = rows.reduce((acc, r) => acc + toNum(r?.credit), 0)
    const diff = debit - credit
    return { debit, credit, diff }
  }, [rows])

  const diffAbs = Math.abs(totals.diff)
  const diffOk = diffAbs < 0.0001

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    setHint(null)

    try {
      const params = new URLSearchParams()
      if (from) params.set('from', from)
      if (to) params.set('to', to)

      const data = await apiFetch(`/api/finance/trial-balance?${params.toString()}`)
      const list = normalizeRows(data)
      setRows(list)
    } catch (e) {
      const msg = String(e?.message || e || '')
      const is404 =
        e?.status === 404 ||
        /\b404\b/.test(msg) ||
        /Cannot GET/i.test(msg) ||
        /not found/i.test(msg)

      setRows([])

      if (is404) {
        setHint('Endpoint manquant: /api/finance/trial-balance (à implémenter côté backend).')
        setErr(null)
      } else {
        setErr(msg || 'Erreur chargement balance.')
      }
    } finally {
      setLoading(false)
    }
  }, [from, to])

  useEffect(() => {
    load()
  }, [load])

  const onExport = useCallback(async (format) => {
    await exportFinance({ from, to, type: 'trial-balance', format })
  }, [from, to])

  return (
    <div className="p-6 space-y-6 min-h-screen">
      <div className="flex flex-wrap gap-3 items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Balance</h1>
          <div className="text-sm text-slate-500 mt-1">
            Période: <span className="font-semibold">{fmtDateFR(from)}</span> → <span className="font-semibold">{fmtDateFR(to)}</span>
          </div>

          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50" to="/finance">
              ← Finance
            </Link>
            <Link className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50" to="/finance/journal">
              Journal →
            </Link>
            <Link className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50" to="/finance/accounts">
              Plan de comptes →
            </Link>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onExport('csv')}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            disabled={loading}
          >
            🧾 Export CSV
          </button>
          <button
            type="button"
            onClick={() => onExport('pdf')}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-950 disabled:opacity-50"
            disabled={loading}
          >
            🖨️ Export PDF
          </button>
        </div>
      </div>

      <FinanceNav />

      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-500 block mb-1">Du</label>
            <input
              type="date"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-slate-300 focus:border-slate-400 pilot-input"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-500 block mb-1">Au</label>
            <input
              type="date"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-slate-300 focus:border-slate-400 pilot-input"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>

          <div className="md:col-span-4">
            <label className="text-xs font-medium text-slate-500 block mb-1">Recherche</label>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-slate-300 focus:border-slate-400 pilot-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="code, libellé…"
            />
          </div>

          <div className="md:col-span-2 flex gap-2">
            <button
              type="button"
              className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-950 disabled:opacity-50 w-full"
              onClick={load}
              disabled={loading}
              title="Actualiser"
            >
              {loading ? '…' : '🔄'}
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-white border border-slate-200">
            <div className="text-xs text-slate-500 font-semibold">Total Débit</div>
            <div className="text-lg font-bold text-slate-900">{fmtMoney(totals.debit)}</div>
          </div>
          <div className="p-3 rounded-lg bg-white border border-slate-200">
            <div className="text-xs text-slate-500 font-semibold">Total Crédit</div>
            <div className="text-lg font-bold text-slate-900">{fmtMoney(totals.credit)}</div>
          </div>
          <div className="p-3 rounded-lg border border-slate-200 bg-white">
            <div className="text-xs text-slate-500 font-semibold">Écart (Débit - Crédit)</div>
            <div className={`text-lg font-bold ${diffOk ? 'text-emerald-700' : 'text-red-700'}`}>
              {fmtMoney(totals.diff)}
            </div>
          </div>
          <div className={`p-3 rounded-lg border ${diffOk ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
            <div className="text-xs font-semibold">Statut</div>
            <div className={`text-lg font-bold ${diffOk ? 'text-emerald-800' : 'text-red-800'}`}>
              {diffOk ? '✅ OK' : '⚠️ À corriger'}
            </div>
            <div className="text-xs mt-1 text-slate-500">
              Une balance saine: total débit = total crédit.
            </div>
          </div>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm">
          <strong>Erreur :</strong> {err}
        </div>
      )}
      {!err && hint && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-900 text-sm">
          <div className="font-semibold">🧩 Balance non branchée</div>
          <div className="mt-1">{hint}</div>
          <div className="mt-2 text-xs text-amber-800">
            Back: créer <code>/api/finance/trial-balance</code> et renvoyer <code>{`{ rows: [...] }`}</code>.
          </div>
        </div>
      )}

      {/* Table */}
      <div className="space-y-2">
        {loading && (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        )}

        {!loading && !err && filtered.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 text-slate-500">
            Aucun compte sur la période.
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 text-xs text-slate-500 font-semibold">
              <div className="col-span-2">Code</div>
              <div className="col-span-4">Libellé</div>
              <div className="col-span-2 text-right">Débit</div>
              <div className="col-span-2 text-right">Crédit</div>
              <div className="col-span-2 text-right">Solde</div>
            </div>

            <div className="divide-y divide-slate-100">
              {filtered.map((a, idx) => {
                const bal = toNum(a?.balance)
                const key = a?.code || a?.id || `${a?.label || 'row'}-${idx}`
                return (
                  <div key={key} className="grid grid-cols-12 gap-3 px-4 py-3 hover:bg-slate-50">
                    <div className="col-span-2 font-semibold text-slate-900 whitespace-nowrap">{a?.code || '—'}</div>
                    <div className="col-span-4 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{a?.label || '—'}</div>
                    </div>
                    <div className="col-span-2 text-right text-sm font-semibold text-slate-900 whitespace-nowrap">
                      {fmtMoney(a?.debit)}
                    </div>
                    <div className="col-span-2 text-right text-sm font-semibold text-slate-900 whitespace-nowrap">
                      {fmtMoney(a?.credit)}
                    </div>
                    <div className={`col-span-2 text-right text-sm font-bold whitespace-nowrap ${bal >= 0 ? 'text-slate-900' : 'text-red-700'}`}>
                      {fmtMoney(bal)}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
